/**
 * Desktop {@link FileStore}, backed by the main process over IPC.
 *
 * The renderer has no filesystem access of its own — that is the point of the
 * sandbox — so every operation is a message. The interface is identical to the
 * browser store's, which is what lets one `App` component serve both without
 * knowing where it is running.
 *
 * `Uint8Array` survives Electron's structured clone in both directions, so
 * bytes cross the boundary without a base64 round trip.
 */
import { FileStoreError } from '@moduviewer/core';
import type {
  FileStat, FileStore, OriginalRef, RecoveryPoint, SaveOutcome, WorkingCopyRef,
} from '@moduviewer/core';

export interface IncomingFileEvent {
  readonly handle: string;
  readonly displayName: string;
  readonly sizeBytes: number;
  readonly modifiedAtMs: number;
  readonly source: OriginalRef['source'];
}

export interface DesktopInfo {
  readonly version: string;
  readonly platform: string;
  readonly locale: string;
  readonly cacheDir: string;
}

/** The surface `preload.cjs` exposes. Nothing wider is reachable. */
interface ModuBridge {
  readonly store: {
    stat(handle: string): Promise<FileStat>;
    readRange(handle: string, start: number, end: number): Promise<Uint8Array>;
    readAll(handle: string): Promise<Uint8Array>;
    createWorkingCopy(original: OriginalRef, id: string): Promise<WorkingCopyRef>;
    writeWorkingCopy(ref: WorkingCopyRef, bytes: Uint8Array): Promise<void>;
    readWorkingCopy(ref: WorkingCopyRef): Promise<Uint8Array>;
    releaseWorkingCopy(ref: WorkingCopyRef): Promise<void>;
    writeRecoveryPoint(id: string, revision: number, bytes: Uint8Array, atMs: number): Promise<RecoveryPoint>;
    listRecoveryPoints(id?: string): Promise<RecoveryPoint[]>;
    readRecoveryPoint(point: RecoveryPoint): Promise<Uint8Array>;
    deleteRecoveryPoints(points: readonly RecoveryPoint[]): Promise<void>;
    exists(name: string): Promise<boolean>;
    saveAs(bytes: Uint8Array, name: string, revision: number): Promise<SaveOutcome>;
    overwriteOriginal(original: OriginalRef, bytes: Uint8Array, revision: number): Promise<SaveOutcome>;
    sweepCache(olderThanMs: number, nowMs: number): Promise<number>;
    stageBytes(bytes: Uint8Array, name: string): Promise<string>;
  };
  readonly shell: {
    showItemInFolder(path: string): Promise<void>;
    openExternal(path: string): Promise<string>;
  };
  readonly app: { info(): Promise<DesktopInfo> };
  pathForFile(file: File): string | null;
  on(channel: string, listener: (payload: unknown) => void): () => void;
}

function bridge(): ModuBridge | null {
  return (window as unknown as { modu?: ModuBridge }).modu ?? null;
}

/** True when running inside the desktop shell. */
export function isDesktop(): boolean {
  return bridge() !== null;
}

export class ElectronFileStore implements FileStore {
  readonly #api: ModuBridge;

  constructor() {
    const api = bridge();
    if (!api) throw new Error('the desktop bridge is not available');
    this.#api = api;
  }

  async stat(handle: string): Promise<FileStat> {
    return this.#api.store.stat(handle);
  }

  async readRange(handle: string, start: number, end: number): Promise<Uint8Array> {
    return wrap(() => this.#api.store.readRange(handle, start, end));
  }

  async readAll(handle: string): Promise<Uint8Array> {
    return wrap(() => this.#api.store.readAll(handle));
  }

  async createWorkingCopy(original: OriginalRef, sessionId: string): Promise<WorkingCopyRef> {
    return wrap(() => this.#api.store.createWorkingCopy(original, sessionId));
  }

  async writeWorkingCopy(ref: WorkingCopyRef, bytes: Uint8Array): Promise<void> {
    return wrap(() => this.#api.store.writeWorkingCopy(ref, bytes));
  }

  async readWorkingCopy(ref: WorkingCopyRef): Promise<Uint8Array> {
    return wrap(() => this.#api.store.readWorkingCopy(ref));
  }

  async releaseWorkingCopy(ref: WorkingCopyRef): Promise<void> {
    return wrap(() => this.#api.store.releaseWorkingCopy(ref));
  }

  async writeRecoveryPoint(
    sessionId: string, revision: number, bytes: Uint8Array, atMs: number,
  ): Promise<RecoveryPoint> {
    return wrap(() => this.#api.store.writeRecoveryPoint(sessionId, revision, bytes, atMs));
  }

  async listRecoveryPoints(sessionId?: string): Promise<RecoveryPoint[]> {
    return wrap(() => this.#api.store.listRecoveryPoints(sessionId));
  }

  async readRecoveryPoint(point: RecoveryPoint): Promise<Uint8Array> {
    return wrap(() => this.#api.store.readRecoveryPoint(point));
  }

  async deleteRecoveryPoints(points: readonly RecoveryPoint[]): Promise<void> {
    return wrap(() => this.#api.store.deleteRecoveryPoints(points));
  }

  async exists(name: string): Promise<boolean> {
    return this.#api.store.exists(name);
  }

  async saveAs(bytes: Uint8Array, suggestedName: string, revision: number): Promise<SaveOutcome> {
    return this.#api.store.saveAs(bytes, suggestedName, revision);
  }

  async overwriteOriginal(
    original: OriginalRef, bytes: Uint8Array, revision: number,
  ): Promise<SaveOutcome> {
    // Unlike the browser, the desktop genuinely can replace the original — and
    // §4.10 still requires the confirmation the controller asks for first.
    return this.#api.store.overwriteOriginal(original, bytes, revision);
  }

  async sweepCache(olderThanMs: number, nowMs: number): Promise<number> {
    return this.#api.store.sweepCache(olderThanMs, nowMs);
  }

  /**
   * Write app-produced bytes into the private cache and return their path.
   *
   * An archive entry has to be a real file before it can be opened like one,
   * and it goes into the app's own cache rather than beside the user's
   * document — §29 is explicit that temporary artefacts must not appear next
   * to the original. The weekly sweep removes them.
   */
  async stageBytes(bytes: Uint8Array, name: string): Promise<string> {
    return wrap(() => this.#api.store.stageBytes(bytes, name));
  }
}

/**
 * An IPC rejection arrives as a plain `Error` with the main process's message
 * flattened into it. Re-typing it here keeps the controller's failure
 * classification working across the boundary.
 */
async function wrap<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation();
  } catch (err) {
    const message = String((err as Error).message ?? err);
    const reason = /ENOSPC|no space/i.test(message) ? 'storage-full'
      : /EACCES|EPERM|permission/i.test(message) ? 'permission-denied'
        : 'io-error';
    throw new FileStoreError(reason, message);
  }
}

/** Files opened by double-click, "Open with", drag-onto-icon or the menu. */
export function onIncomingFile(listener: (file: IncomingFileEvent) => void): () => void {
  const api = bridge();
  if (!api) return () => undefined;
  return api.on('modu:open-file', (payload) => listener(payload as IncomingFileEvent));
}

/** Menu accelerators the main process forwards rather than handling itself. */
export function onDesktopCommand(listener: (command: string) => void): () => void {
  const api = bridge();
  if (!api) return () => undefined;
  return api.on('modu:command', (payload) => listener(String(payload)));
}

export function onOpenFailed(
  listener: (info: { path: string; messageKey: string }) => void,
): () => void {
  const api = bridge();
  if (!api) return () => undefined;
  return api.on('modu:open-failed', (payload) =>
    listener(payload as { path: string; messageKey: string }));
}

/**
 * The path behind a file dropped onto the desktop window, or `null` in the
 * browser (where no such thing exists and none should).
 */
export function desktopPathForFile(file: File): string | null {
  return bridge()?.pathForFile(file) ?? null;
}

export async function desktopInfo(): Promise<DesktopInfo | null> {
  return bridge()?.app.info() ?? null;
}

export async function revealInFolder(path: string): Promise<void> {
  await bridge()?.shell.showItemInFolder(path);
}

export async function openInDefaultApp(path: string): Promise<void> {
  await bridge()?.shell.openExternal(path);
}
